import React from 'react'
import cn from 'classnames'
import * as yup from 'yup'
import { Field, useFormikContext } from 'formik'
import { Checkbox } from 'components'
import { damageDescription } from './damageDescription'
import { FormDamageReport } from '../types'
import classes from './SummaryFields.module.scss'
import { useTranslation } from 'react-i18next'

export const validationSchema = yup.object({
  summaryRead: yup
    .boolean()
    .required()
    .oneOf(
      [true],
      'pages.damageReports.damageReportForm.summaryFields.summaryAccept'
    ),
})
export const SummaryFields: React.FunctionComponent = () => {
  const { t } = useTranslation()
  const { values } = useFormikContext<FormDamageReport>()

  return (
    <>
      <p>
        {t(
          'pages.damageReports.damageReportForm.summaryFields.validateMessage'
        )}
      </p>

      <table className={cn('wb-table', 'wb-table--striped', classes.table)}>
        <tbody>
          <tr>
            <th scope="row">
              {t(
                'pages.damageReports.damageReportForm.summaryFields.tableStrings.licensePlate'
              )}
            </th>
            <td>{values.licensePlate}</td>
          </tr>
          <tr>
            <th scope="row">
              {t(
                'pages.damageReports.damageReportForm.summaryFields.tableStrings.when'
              )}
            </th>
            <td>
              {values.date} {values.time}
            </td>
          </tr>
          <tr>
            <th scope="row">
              {t(
                'pages.damageReports.damageReportForm.summaryFields.tableStrings.where'
              )}
            </th>
            <td>{`${values.street}, ${values.postcode} ${values.city}`}</td>
          </tr>
          <tr>
            <th scope="row">
              {t(
                'pages.damageReports.damageReportForm.summaryFields.tableStrings.readyToDrive'
              )}
            </th>
            <td>{values.vehicleDrivable ? t('global.yes') : t('global.no')}</td>
          </tr>
          {!values.vehicleDrivable && (
            <tr>
              <th scope="row">
                {t(
                  'pages.damageReports.damageReportForm.summaryFields.tableStrings.vehiclePlace'
                )}
              </th>
              <td>{`${values.vehicleLocation.street}, ${values.vehicleLocation.postcode} ${values.vehicleLocation.city}, ${values.vehicleLocation.description}`}</td>
            </tr>
          )}
          <tr>
            <th scope="row">
              {t(
                'pages.damageReports.damageReportForm.summaryFields.tableStrings.contactPolice'
              )}
            </th>
            <td>
              {values.hadPoliceReport
                ? `Ja, am ${values.policeReport}`
                : t('global.no')}
            </td>
          </tr>
          <tr>
            <th scope="row">
              {t(
                'pages.damageReports.damageReportForm.summaryFields.tableStrings.who'
              )}
            </th>
            <td>
              {values.reporterIsDriver
                ? t('pages.damageReports.damageReportForm.driverFields.self')
                : t('pages.damageReports.damageReportForm.driverFields.other')}
            </td>
          </tr>
          {values.reporterIsDriver ? (
            <>
              <tr>
                <th scope="row">
                  {t(
                    'pages.damageReports.damageReportForm.summaryFields.tableStrings.validLicense'
                  )}
                </th>
                <td>
                  {values.driverHadLicense ? t('global.yes') : t('global.no')}
                </td>
              </tr>
              <tr>
                <th scope="row">
                  {t(
                    'pages.damageReports.damageReportForm.summaryFields.tableStrings.alcohol'
                  )}
                </th>
                <td>
                  {values.driverHadDrugs
                    ? `Ja, ${values.driverDrugs}`
                    : t('global.no')}
                </td>
              </tr>
            </>
          ) : (
            <>
              <tr>
                <th scope="row">
                  {t(
                    'pages.damageReports.damageReportForm.summaryFields.tableStrings.name'
                  )}
                </th>
                <td>
                  {values.driver.firstName} {values.driver.lastName}
                </td>
              </tr>
              <tr>
                <th scope="row">
                  {t(
                    'pages.damageReports.damageReportForm.summaryFields.tableStrings.address'
                  )}
                </th>
                <td>{`${values.driver.street}, ${values.driver.postcode} ${values.driver.city}`}</td>
              </tr>
              <tr>
                <th scope="row">{t('global.phone')}</th>
                <td>{values.driver.phone}</td>
              </tr>
              <tr>
                <th scope="row">
                  {t(
                    'pages.damageReports.damageReportForm.summaryFields.tableStrings.birthday'
                  )}
                </th>
                <td>{values.driver.birthday}</td>
              </tr>
              <tr>
                <th scope="row">
                  {t(
                    'pages.damageReports.damageReportForm.summaryFields.tableStrings.consent'
                  )}
                </th>
                <td>
                  {values.driver.hadPermission
                    ? t('global.yes')
                    : t('global.no')}
                </td>
              </tr>
              {!values.driver.hadPermission && (
                <tr>
                  <th scope="row">
                    {t(
                      'pages.damageReports.damageReportForm.summaryFields.tableStrings.permissionNo'
                    )}
                  </th>
                  <td>{values.driver.permission}</td>
                </tr>
              )}
              <tr>
                <th scope="row">
                  {t(
                    'pages.damageReports.damageReportForm.summaryFields.tableStrings.validLicenseDriver'
                  )}
                </th>
                <td>
                  {values.driverHadLicense ? t('global.yes') : t('global.no')}
                </td>
              </tr>
              <tr>
                <th scope="row">
                  {t(
                    'pages.damageReports.damageReportForm.summaryFields.tableStrings.trueKnow'
                  )}
                </th>
                <td>
                  {values.driver.licenseAware
                    ? t('global.yes')
                    : t('global.no')}
                </td>
              </tr>
              <tr>
                <th scope="row">
                  {t(
                    'pages.damageReports.damageReportForm.summaryFields.tableStrings.alcoholDriver'
                  )}
                </th>
                <td>
                  {values.driverHadDrugs
                    ? t('global.yes') + `, ${values.driverDrugs}`
                    : t('global.no')}
                </td>
              </tr>
            </>
          )}
          <tr>
            <th scope="row">
              {t(
                'pages.damageReports.damageReportForm.summaryFields.tableStrings.what'
              )}
            </th>
            <td>{damageDescription(values.damage, t)}</td>
          </tr>
          <tr>
            <th scope="row">
              {t(
                'pages.damageReports.damageReportForm.summaryFields.tableStrings.otherVehicle'
              )}
            </th>
            <td>
              {values.multipleVehicles ? t('global.yes') : t('global.no')}
            </td>
          </tr>
          {values.multipleVehicles && values.otherVehicleDamage && (
            <tr>
              <th scope="row">
                {t(
                  'pages.damageReports.damageReportForm.summaryFields.tableStrings.otherVehicleDamage'
                )}
              </th>
              <td>{damageDescription(values.otherVehicleDamage, t)}</td>
            </tr>
          )}
          <tr>
            <th scope="row">
              {t(
                'pages.damageReports.damageReportForm.summaryFields.tableStrings.trailer'
              )}
            </th>
            <td>{values.hadTrailer ? t('global.yes') : t('global.no')}</td>
          </tr>
          <tr>
            <th scope="row">
              {t(
                'pages.damageReports.damageReportForm.summaryFields.tableStrings.moreDamage'
              )}
            </th>
            <td>
              {values.otherDamages
                ? t('global.yes') + `, ${values.otherDamages}`
                : t('global.no')}
            </td>
          </tr>
        </tbody>
      </table>

      <p>
        {t('pages.damageReports.damageReportForm.summaryFields.summaryInfo')}
      </p>

      <Field
        component={Checkbox}
        id="summaryRead"
        name="summaryRead"
        label={t(
          'pages.damageReports.damageReportForm.summaryFields.summaryRead'
        )}
        required
      />
    </>
  )
}
